import { useMutation } from '@tanstack/react-query';
import type { GeoLocationDTO } from '@bifeai/shared';
import { Button, useToast } from '@bifeai/ui';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { RouteMap } from './RouteMap';

const SOURCE: Record<string, string> = {
  AUTO: 'Encontrada con la dirección',
  MANUAL: 'Marcada a mano',
  NOT_FOUND: 'No se encontró la dirección: tocá el mapa donde está',
};

/**
 * Ubicación en el mapa de un cliente o de un local: se busca con la dirección o se toca el mapa para marcarla.
 * `base` es la ruta de la API (…/customers/:id o …/branches/:id).
 */
export function LocationPicker({
  base,
  initial,
  canSearch = true,
  onSaved,
}: {
  base: string;
  initial: GeoLocationDTO;
  canSearch?: boolean;
  onSaved?: (g: GeoLocationDTO) => void;
}) {
  const toast = useToast();
  const [g, setG] = useState<GeoLocationDTO>(initial);
  const pos = g.lat !== null && g.lng !== null ? { lat: g.lat, lng: g.lng } : null;
  const done = (r: GeoLocationDTO) => {
    setG(r);
    onSaved?.(r);
  };
  const save = useMutation({
    mutationFn: (p: { lat: number; lng: number }) => api.put<GeoLocationDTO>(`${base}/location`, p),
    onSuccess: (r) => (done(r), toast.success('Ubicación guardada')),
    onError: (e) => toast.error('No se pudo guardar', errorMessage(e)),
  });
  const search = useMutation({
    mutationFn: () => api.post<GeoLocationDTO>(`${base}/locate`),
    onSuccess: (r) => (
      done(r),
      r.lat === null ? toast.error('No se encontró la dirección', 'Tocá el mapa donde está.') : toast.success('Ubicación encontrada')
    ),
    onError: (e) => toast.error('No se pudo buscar', errorMessage(e)),
  });
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="bf-cell-sub" style={{ flex: 1 }}>
          {g.source ? SOURCE[g.source] : pos ? 'Ubicado' : 'Todavía sin ubicación'}. Tocá el mapa para corregirla.
        </span>
        {canSearch && (
          <Button size="sm" icon={<Search size={14} />} loading={search.isPending} onClick={() => search.mutate()}>
            Buscar con la dirección
          </Button>
        )}
      </div>
      <RouteMap pick={pos} onPick={(p) => save.mutate(p)} height={220} />
    </div>
  );
}
